/**
 * @fileoverview Local-first system-web service for legacy migration receipts, runs, and live grid fetch.
 * @module system-web/legacy-migration-service
 *
 * Responsibilities:
 * - Provide liveQuery-safe Dexie reads for migration receipts and run history.
 * - Encapsulate the online-exception live legacy-grid fetch (Streamline SQL data).
 * - Initiate migration runs via POST to system-api and write receipts through locally on success.
 *
 * Dependencies:
 * - `@firesquirrel/local-db` for system Dexie access.
 * - `../apiClient` `apiFetch` for online-exception and mutation operations.
 *
 * Architecture:
 * - Receipts and runs are local-first: Dexie mirrors of system Mongo, synced via system pull.
 * - Live grid rows (SQL data) are an online exception: fetched fresh per grid open, never cached in Dexie.
 * - startRun writes through locally on HTTP 201 to give instant run-history feedback before sync.
 * - No component calls apiFetch directly; all network I/O is here.
 *
 * Notes:
 * - `fetchLiveLegacyGrid` must be called from a `useEffect`, never inside `liveQuery` (§4.3 of impl plan).
 * - Receipts overlay "already migrated" badges on grid rows — service consumers merge these in the hook.
 */

import { getSystemDb } from '@firesquirrel/local-db';
import { apiFetch } from '../apiClient';

// ---------------------------------------------------------------------------
// DTOs — lightweight projection types for what this service surfaces to hooks
// ---------------------------------------------------------------------------

/**
 * @contract MigrationReceiptRow
 * Purpose:  Local-first read shape for a single migration receipt from Dexie.
 * Fields:   Minimal fields needed by the grid overlay and run-history panel.
 * Rules:    Populated by system sync pull; service never writes receipts locally
 *           (server-authoritative). startRun is the sole exception — see below.
 * Sync:     Source of truth: system Mongo `legacyMigrationReceipts`; Dexie mirrors via sync.
 */
export interface MigrationReceiptRow {
  _id: string;
  tenantId: string;
  datasetKey: string;
  legacyDb: string;
  legacyDeptId: number;
  legacySourceId: string;
  mongoIds: string[];
  score: number;
  band: string;
  runId: string;
  migratedAt: string;
  _deleted?: boolean;
}

/**
 * @contract MigrationRunRow
 * Purpose:  Local-first read shape for a single migration run record from Dexie.
 * Fields:   Fields needed by the run-history panel.
 * Rules:    Server-authoritative; Dexie mirrors via sync.
 * Sync:     Source of truth: system Mongo `legacyMigrationRuns`; Dexie mirrors via sync.
 */
export interface MigrationRunStepRow {
  adapterKey: string;
  displayName: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped' | 'cancelled';
  percent: number;
  counts: { selected: number; created: number; updated: number; skipped: number; failed: number };
  startedAt?: string;
  finishedAt?: string;
  error?: string;
  phase?: 'extracting' | 'writing';
  processed?: number;
  total?: number;
  message?: string;
  heartbeatAt?: string;
  failedRows?: Array<{ legacyId: string; error: string }>;
  failedReasonSummary?: Array<{ error: string; count: number }>;
  overlayRunId?: string;
  overlayStartedAt?: string;
}

export interface MigrationRunRow {
  _id: string;
  tenantId: string;
  datasetKey: string;
  legacyDb: string;
  legacyDeptId: number;
  mode: 'dry-run' | 'apply';
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  startedAt: string;
  finishedAt?: string;
  counts: { selected: number; created: number; updated: number; skipped: number; failed: number };
  byRow: Array<{ legacyId: string; action: string; mongoIds: string[]; score: number; error?: string }>;
  errors: unknown[];
  initiatedBy: string;
  _deleted?: boolean;
  isGlobal?: boolean;
  parentRunId?: string;
  steps?: MigrationRunStepRow[];
  overallPercent?: number;
  rehostStatus?: 'not_started' | 'running' | 'completed' | 'partial_failed';
  rehostCounts?: {
    copied: number;
    pending: number;
    failed: number;
    // Optional to match departmentWideStepper's RehostCounts — a synced run
    // document may omit the field.
    skipped?: number;
    total: number;
  };
  rehostErrorSummary?: Array<{ errorType: string; count: number }>;
  cancelRequested?: boolean;
  failedRows?: Array<{ legacyId: string; error: string }>;
  failedReasonSummary?: Array<{ error: string; count: number }>;
  message?: string;
  phase?: 'extracting' | 'writing';
  processed?: number;
  total?: number;
  heartbeatAt?: string;
  updatedAt?: string;
}

/**
 * @contract ScoredGridRow
 * Purpose:  Represents a single row returned by fetchLiveLegacyGrid.
 *           This is the online-exception data shape — SQL-sourced, never persisted to Dexie.
 * Fields:   legacyId, cells (adapter-column values), score, band, issues, migratedReceipt (overlay).
 * Rules:    Adapter gridColumns drive the cell keys; columns declared on the DatasetAdapter.
 * Sync:     NOT synced. Held in component state per grid session.
 */
export interface ScoredGridRow {
  legacyId: string;
  cells: Record<string, unknown>;
  score: number;
  band: 'green' | 'yellow' | 'red' | 'blocked';
  issues: Array<{ ruleId: string; severity: string; message: string }>;
  /** Supplementary AI annotation (display-only; present only when the AI flag is enabled server-side). */
  ai?: {
    aiConfidence: 'high' | 'medium' | 'low';
    concerns: string[];
    generatedAt: string;
    contentHash: string;
  };
  migratedReceipt?: MigrationReceiptRow;
}

/**
 * @contract StartRunInput
 * Purpose:  Body for startRun() — mirrors the Zod schema on the server.
 * Fields:   tenantId, mode, selection, bundleDeps, parentRunId for linked dataset reruns.
 * Rules:    selection must be non-empty.
 * Sync:     POST creates run doc server-side; Dexie pulls it via system sync.
 */
export interface StartRunInput {
  tenantId: string;
  mode: 'dry-run' | 'apply';
  selection: string[];
  bundleDeps?: boolean;
  parentRunId?: string;
}

/**
 * @contract DatasetAdapterMeta
 * Purpose:  Server-provided metadata for a registered migration adapter.
 * Fields:   key, displayName, targetCollections, gridColumns, composite, manifest.
 * Rules:    Hydrates the page's dataset tabs, grid columns, and manifest-editor baseline.
 * Sync:     Fetched once per page load from GET /api/system/legacy-migration/datasets (online, not cached in Dexie).
 */
export interface DatasetAdapterMeta {
  key: string;
  displayName: string;
  targetCollections: string[];
  gridColumns: Array<{ key: string; header: string; width?: number; render?: string }>;
  composite: { childAdapterKeys: string[]; dependencyResolver: string } | null;
  manifest: {
    key: string;
    displayName: string;
    kind: 'single' | 'composite';
    source: {
      primaryTable: { schema: string; table: string; primaryKey: string; columns: string[] };
      childTables: Array<{ schema: string; table: string; primaryKey: string; columns: string[] }>;
      relationships: Array<{
        parent: { table: string; column: string };
        child: { table: string; column: string };
        cardinality: 'one-to-one' | 'one-to-many';
        enforcement: string;
        orphanPolicy: 'block' | 'warn' | 'drop' | 'includeAsRoot';
        orphanPolicyJustification?: string;
      }>;
      filters?: Array<{ column: string; op: string; value?: unknown }>;
      tenantScopeColumn: string;
    };
    target: { collections: Array<{ collection: string; description: string }> };
  };
}

// ---------------------------------------------------------------------------
// Online reads (adapter metadata — not cached in Dexie)
// ---------------------------------------------------------------------------

/**
 * @localFirst
 * Source of Truth: server REGISTERED_ADAPTERS (static adapter registry).
 * Write Flow:      Not applicable — read-only metadata.
 * Sync Strategy:   Online fetch per page load; held in component state, never persisted to Dexie.
 * Conflict Resolution: Not applicable.
 *
 * Returns the registered adapter metadata (keys, gridColumns, manifests) used to hydrate the
 * migration page's dataset tabs, grid columns, and manifest-editor baseline.
 *
 * @errorHandling
 * Strategy:   Throws on non-OK HTTP; caller falls back to static tab stubs.
 * User Impact: Page still renders with default tabs if the fetch fails.
 * Logging:    REQUIRED — caller logs failures.
 */
export async function fetchDatasets(): Promise<DatasetAdapterMeta[]> {
  const result = await apiFetch('/api/system/legacy-migration/datasets');
  return (result.datasets ?? []) as DatasetAdapterMeta[];
}

// ---------------------------------------------------------------------------
// Dexie reads (liveQuery-safe — pure reads, no writes)
// ---------------------------------------------------------------------------

/**
 * @localFirst
 * Source of Truth: system Dexie `legacy_migration_receipts` (mirrored from Mongo).
 * Write Flow:      Not applicable — service never writes receipts (server-authoritative).
 * Sync Strategy:   System sync pull mirrors legacyMigrationReceipts from Mongo → Dexie.
 * Conflict Resolution: Not applicable — read-only.
 *
 * Return value is suitable for use inside `liveQuery(() => listReceipts(...))`.
 * NEVER write to Dexie from inside liveQuery.
 */
export async function listReceipts(
  tenantId: string,
  datasetKey: string,
): Promise<MigrationReceiptRow[]> {
  const db = await getSystemDb();
  const all = await db.legacy_migration_receipts.toArray();
  return (all as MigrationReceiptRow[]).filter(
    (r) => r.tenantId === tenantId && r.datasetKey === datasetKey && !r._deleted,
  );
}

/**
 * @localFirst
 * Source of Truth: system Dexie `legacy_migration_runs` (mirrored from Mongo).
 * Write Flow:      startRun() inserts a provisional row immediately on success.
 * Sync Strategy:   System sync pull authoritative pull; provisional row replaced on next sync.
 * Conflict Resolution: Server wins — pull overwrites local provisional rows.
 *
 * Return value is suitable for use inside `liveQuery(() => listRuns(...))`.
 * NEVER write to Dexie from inside liveQuery.
 */
export async function listRuns(
  tenantId: string,
  datasetKey: string,
): Promise<MigrationRunRow[]> {
  const db = await getSystemDb();
  const all = await db.legacy_migration_runs.toArray();
  const filtered = (all as MigrationRunRow[]).filter((r) => {
    if (r.tenantId !== tenantId || r._deleted) return false;
    return r.datasetKey === datasetKey;
  });
  // Sort newest first for the history panel.
  return filtered.sort((a, b) => (a.startedAt < b.startedAt ? 1 : -1));
}

// ---------------------------------------------------------------------------
// Online-exception: live legacy grid fetch
// ---------------------------------------------------------------------------

/**
 * @contract PaginatedGridResult
 * Purpose:  Server-side paginated response from the migration grid route.
 * Fields:   rows (current page), total (full count), page (1-based), pageSize,
 *           physicalDatabase (the actual Streamline database queried), appEnv.
 * Rules:    page is 1-based; total is the count of all scored rows (not just the page).
 * Sync:     NOT synced. Held in component state per grid session.
 */
export interface PaginatedGridResult {
  rows: ScoredGridRow[];
  total: number;
  page: number;
  pageSize: number;
  unmetDeps?: string[];
  perTableCounts?: Record<string, number>;
  /** The actual physical Streamline database name queried (e.g. 'Dev_SLInspections001') — differs
   *  from the logical dataset key in non-prod environments. See resolvePhysicalLegacyDbName. */
  physicalDatabase?: string;
  appEnv?: string;
}

/**
 * @localFirst
 * Source of Truth: Streamline SQL Server (online exception — not in our system).
 * Write Flow:      NOT written to Dexie — held in component state per grid session.
 * Sync Strategy:   Not applicable — fresh fetch per grid open.
 * Conflict Resolution: Not applicable.
 *
 * MUST be called from a `useEffect`, never inside `liveQuery`.
 * Returns paginated ScoredRow array + total count; the hook merges receipt overlays client-side.
 *
 * @errorHandling
 * Strategy:   Throws on non-OK HTTP responses (handled by the hook's error state).
 * User Impact: Grid shows error state with retry button.
 * Logging:    REQUIRED — log fetch failures with tenantId + datasetKey.
 */
export async function fetchLiveLegacyGrid(
  tenantId: string,
  datasetKey: string,
  page = 1,
  pageSize = 50,
  band?: string,
  sortBy?: string,
  sortDir?: 'asc' | 'desc',
  search?: string,
  columnFilters?: Record<string, string>,
  queryJson?: string,
): Promise<PaginatedGridResult> {
  const params = new URLSearchParams({
    tenantId,
    page: String(page),
    pageSize: String(pageSize),
  });
  if (band && band !== 'all') params.set('band', band);
  if (sortBy) params.set('sortBy', sortBy);
  if (sortDir) params.set('sortDir', sortDir);
  if (search && search.trim()) params.set('search', search.trim());
  if (columnFilters) {
    for (const [key, value] of Object.entries(columnFilters)) {
      if (value?.trim()) params.set(`filter.${key}`, value.trim());
    }
  }
  if (queryJson?.trim()) params.set('query', queryJson.trim());

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 180_000);
  let result: any;
  try {
    result = await apiFetch(
      `/api/system/legacy-migration/${encodeURIComponent(datasetKey)}/grid?${params.toString()}`,
      { signal: controller.signal },
    );
  } catch (err: any) {
    if (err?.name === 'AbortError') {
      throw new Error(`Request timed out after 180000ms: /api/system/legacy-migration/${datasetKey}/grid`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
  const rows = (result.rows ?? []) as ScoredGridRow[];
  return {
    rows,
    // Fallback: if backend doesn't provide total yet, use rows.length as a minimum.
    total: result.total ?? rows.length,
    page: result.page ?? page,
    pageSize: result.pageSize ?? pageSize,
    unmetDeps: Array.isArray(result.unmetDeps) ? result.unmetDeps : [],
    perTableCounts: result.perTableCounts && typeof result.perTableCounts === 'object' ? result.perTableCounts : undefined,
    physicalDatabase: result.physicalDatabase,
    appEnv: result.appEnv,
  };
}


/**
 * @contract LegacyDepartment
 * Purpose:  Raw department row from a Streamline legacy SQL database, as
 *           returned by GET /api/legacy/:database/departments.
 * Rules:    Online exception — never cached in Dexie; used to auto-fill the
 *           onboarding wizard.
 * Sync:     Not applicable.
 */
export interface LegacyDepartment {
  TenantId: number | null;
  frusa_dept_dept_Id: number | null;
  dept_id: number;
  weburi: string | null;
  shortname: string | null;
  name: string | null;
  number: string | null;
  st_prefix: string | null;
  street: string | null;
  st_type: string | null;
  st_suffix: string | null;
  addr_2: string | null;
  apt_room: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  fax: string | null;
  phone: string | null;
  dept_logo: string | null;
  dept_logo_md5: string | null;
  color_main: string | null;
  color_main_dark: string | null;
  color_main_darker: string | null;
  color_grey_main: string | null;
  color_grey_dark: string | null;
  color_grey_darker: string | null;
  createdby: number | null;
  modifiedby: number | null;
  datecreated: string | null;
  datemodified: string | null;
  privacyURL: string | null;
  termsURL: string | null;
  contactURL: string | null;
  faqlink: string | null;
  materialtablelink: string | null;
  additionalinfolink: string | null;
  permitfeelink: string | null;
  DefaultPageText: string | null;
  dept_image: string | null;
  email_report: string | null;
  cc_report_email: string | null;
  report_sender_email: string | null;
  DefaultPageTitle: string | null;
  include_insp_comments: boolean | null;
  pwd_reset_days: number | null;
  frusa_admin_id: number | null;
  login_image: string | null;
  UseFireRecoveryBilling: boolean | null;
  DepartmentTheme: string | null;
  f_frusa_endpoint_id: number | null;
  Enabled: boolean | null;
  ClientMainGUID: string | null;
  ClientIndustryId: number | null;
  ServiceTierId: number | null;
  IsLocal: boolean | null;
  use_smtp: boolean | null;
  default_reply_to: string | null;
  RHDept_id: number | null;
  IsAPIEnabled: boolean | null;
  IsNewSLISystem: boolean | null;
  IsBacktoVer6: boolean | null;
}

/**
 * @localFirst
 * Source of Truth: Streamline SQL Server (online exception — not in our system).
 * Write Flow:      NOT written to Dexie — held in hook state for the wizard session.
 * Sync Strategy:   Not applicable — fetched on demand.
 * Conflict Resolution: Not applicable.
 *
 * @errorHandling
 * Strategy:   Throws on non-OK HTTP responses (handled by the hook's error state).
 * User Impact: Wizard shows an inline error with retry.
 * Logging:    Hook surfaces the error message.
 */
export async function fetchLegacyDepartments(
  database: string,
): Promise<{ departments: LegacyDepartment[]; physicalDatabase: string | null }> {
  const result = await apiFetch(`/api/legacy/${encodeURIComponent(database)}/departments`);
  return {
    departments: (result.departments ?? []) as LegacyDepartment[],
    // The physical Streamline DB the server actually queried (may differ from the
    // logical dropdown value in non-prod) — shown as a confirmation badge.
    physicalDatabase: (result.physicalDatabase as string | undefined) ?? null,
  };
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/**
 * @localFirst
 * Source of Truth: system Mongo `legacyMigrationRuns` (server-authoritative).
 * Write Flow:
 *   1. POST to /api/system/legacy-migration/:datasetKey/runs.
 *   2. On HTTP 202 (apply) or 201 (dry-run): insert a provisional run row into
 *      system Dexie only when sync has not already delivered the authoritative row.
 *      The authoritative run doc arrives via direct fetch or system sync pull.
 *   3. On error: throw; let the hook surface the error.
 * Sync Strategy:   System sync pull replaces the provisional row with the authoritative one.
 * Conflict Resolution: Server wins.
 *
 * @errorHandling
 * Strategy:   Throws on non-OK responses. Caller (hook) surfaces the error to the UI.
 * User Impact: Confirmation modal shows error; run is not persisted locally.
 * Logging:    REQUIRED — log failures with datasetKey + tenantId.
 */
export async function startRun(
  datasetKey: string,
  input: StartRunInput,
): Promise<{ runId: string }> {
  const result = await apiFetch(
    `/api/system/legacy-migration/${encodeURIComponent(datasetKey)}/runs`,
    { method: 'POST', body: JSON.stringify(input) },
  );

  // Write provisional run row into Dexie for instant feedback
  // (server-authoritative pull will replace it on next sync cycle).
  try {
    const db = await getSystemDb();
    const isDepartmentWide = datasetKey === DEPARTMENT_WIDE_KEY;
    const provisional = {
      _id: result.runId ?? `provisional-${Date.now()}`,
      tenantId: input.tenantId,
      datasetKey,
      mode: input.mode,
      status: input.mode === 'apply' ? 'running' as const : 'pending' as const,
      startedAt: new Date().toISOString(),
      counts: {
        selected: input.selection.includes('*') ? 0 : input.selection.length,
        created: 0,
        updated: 0,
        skipped: 0,
        failed: 0,
      },
      byRow: [],
      errors: [],
      initiatedBy: '',
      _deleted: false,
      ...(input.parentRunId ? { parentRunId: input.parentRunId } : {}),
      ...(isDepartmentWide
        ? {
            isGlobal: true,
            overallPercent: 0,
            steps: DEPARTMENT_WIDE_PROVISIONAL_STEPS,
          }
        : {}),
    };
    const existing = await db.legacy_migration_runs.get(provisional._id);
    if (!existing) await db.legacy_migration_runs.put(provisional as any);
  } catch (localErr) {
    // Local write failure is non-fatal — sync will pull the authoritative row shortly.
    console.warn('[legacyMigrationService] Failed to write provisional run row:', localErr);
  }

  return { runId: result.runId };
}

/**
 * @localFirst
 * Source of Truth: system Mongo legacyMigrationRuns (server-authoritative).
 * Write Flow: POST cancel, then persist the server-acknowledged stopped state in Dexie.
 * Sync Strategy: next fetchAndSyncRuns replaces the local row.
 * Conflict Resolution: server wins.
 */
/**
 * @process markLocalRunCancelled
 * Steps: load Dexie run; write status cancelled + cancelRequested + finishedAt
 * Inputs: runId, optional operator message
 * Outputs: none
 * Failure Handling: local Dexie errors are logged, never thrown
 * Idempotency: REQUIRED
 */
async function markLocalRunCancelled(runId: string, message?: string): Promise<void> {
  try {
    const db = await getSystemDb();
    const existing = await db.legacy_migration_runs.get(runId);
    if (!existing) return;
    await db.legacy_migration_runs.put({
      ...existing,
      cancelRequested: true,
      status: 'cancelled',
      finishedAt: existing.finishedAt ?? new Date().toISOString(),
      message: message || existing.message || 'Stopped by operator',
    } as any);
  } catch (localErr) {
    console.warn('[legacyMigrationService] Failed to mark local run cancelled:', localErr);
  }
}

export async function cancelRun(runId: string): Promise<{ runId: string; status: string }> {
  try {
    const result = await apiFetch(`/api/system/legacy-migration/runs/${encodeURIComponent(runId)}/cancel`, {
      method: 'POST',
    });
    await markLocalRunCancelled(runId);
    return { runId: result.runId ?? runId, status: result.status ?? 'cancelled' };
  } catch (err) {
    const status = (err as { status?: number }).status;
    // 409: server already left the run (worker died, already cancelled). Clear the
    // leftover Dexie row so DatasetProgressStrip can dismiss.
    if (status === 409) {
      await markLocalRunCancelled(runId, 'Stopped leftover job');
      return { runId, status: 'cancelled' };
    }
    throw err;
  }
}

/**
 * @localFirst
 * Source of Truth: system Mongo legacyMigrationRuns (server-authoritative).
 * Write Flow: POST resume, then mark Dexie status running on the same runId.
 * Sync Strategy: next fetchAndSyncRuns replaces the local row.
 * Conflict Resolution: server wins.
 */
export async function resumeRun(runId: string): Promise<{ runId: string; status: string }> {
  const result = await apiFetch(`/api/system/legacy-migration/runs/${encodeURIComponent(runId)}/resume`, {
    method: 'POST',
  });
  try {
    const db = await getSystemDb();
    const existing = await db.legacy_migration_runs.get(runId);
    if (existing) {
      await db.legacy_migration_runs.put({
        ...existing,
        status: 'running',
        cancelRequested: false,
        finishedAt: undefined,
      } as any);
    }
  } catch (localErr) {
    console.warn('[legacyMigrationService] Failed to mark local run resumed:', localErr);
  }
  return { runId: result.runId ?? runId, status: result.status ?? 'running' };
}

/**
 * @localFirst
 * Source of Truth: system Mongo legacyFileTransfers + legacyMigrationRuns (server-authoritative).
 * Write Flow: POST retry-file-copy, then mark Dexie rehostStatus running on the same runId.
 * Sync Strategy: next fetchAndSyncRuns replaces the local row.
 * Conflict Resolution: server wins. Occupancies and other adapters are not re-run.
 */
export async function retryFailedFileCopy(runId: string): Promise<{ runId: string; resetCount: number; rehostStatus: string }> {
  const result = await apiFetch(`/api/system/legacy-migration/runs/${encodeURIComponent(runId)}/retry-file-copy`, {
    method: 'POST',
  });
  try {
    const db = await getSystemDb();
    const existing = await db.legacy_migration_runs.get(runId);
    if (existing) {
      await db.legacy_migration_runs.put({
        ...existing,
        rehostStatus: 'running',
      } as any);
    }
  } catch (localErr) {
    console.warn('[legacyMigrationService] Failed to mark local file copy retry running:', localErr);
  }
  return {
    runId: result.runId ?? runId,
    resetCount: Number(result.resetCount ?? 0),
    rehostStatus: result.rehostStatus ?? 'running',
  };
}

/**
 * @localFirst
 * Source of Truth: Dexie legacy_migration_runs.
 * Write Flow: none (pure read).
 * Sync Strategy: fetchAndSyncRuns from the hook poller.
 * Conflict Resolution: server wins.
 */
export async function getMigrationRun(runId: string): Promise<MigrationRunRow | null> {
  const db = await getSystemDb();
  const row = await db.legacy_migration_runs.get(runId);
  if (!row || (row as MigrationRunRow)._deleted) return null;
  return row as MigrationRunRow;
}

/**
 * @localFirst
 * Source of Truth: Dexie legacy_migration_runs (pure read).
 * Write Flow: none.
 * Sync Strategy: report-page poller + startRun provisional insert.
 * Conflict Resolution: server wins.
 *
 * Returns the newest single-dataset run for this tenant+adapter started at or
 * after `sinceIso` (the parent department-wide run). Used to overlay live
 * re-run counts on a frozen historical step tile.
 */
export async function listLatestChildDatasetRun(
  tenantId: string,
  adapterKey: string,
  sinceIso?: string,
): Promise<MigrationRunRow | null> {
  const byAdapter = await listChildDatasetRunsForParent(tenantId, [adapterKey], sinceIso);
  return byAdapter[adapterKey]?.[0] ?? null;
}

/**
 * @localFirst
 * Source of Truth: Dexie legacy_migration_runs (pure read).
 * Write Flow: none.
 * Sync Strategy: Parent dashboard subscribes with liveQuery; active attempts are refreshed separately.
 * Conflict Resolution: Server-authoritative rows are ordered newest-first per adapter.
 *
 * Returns every single-dataset attempt started after a department-wide parent run.
 */
export async function listChildDatasetRunsForParent(
  tenantId: string,
  adapterKeys: readonly string[],
  sinceIso?: string,
  parentRunId?: string,
): Promise<Record<string, MigrationRunRow[]>> {
  const db = await getSystemDb();
  const all = await db.legacy_migration_runs.toArray();
  const keySet = new Set(adapterKeys);
  const since = sinceIso ? Date.parse(sinceIso) : 0;
  const rows = (all as MigrationRunRow[])
    .filter((r) => {
      if (r._deleted || r.tenantId !== tenantId || !keySet.has(r.datasetKey)) return false;
      if (r.isGlobal || r.datasetKey === DEPARTMENT_WIDE_KEY) return false;
      if (parentRunId && r.parentRunId && r.parentRunId !== parentRunId) return false;
      if (!Number.isFinite(since) || since <= 0) return true;
      const started = Date.parse(r.startedAt);
      return Number.isFinite(started) && started >= since;
    })
    .sort((a, b) => String(b.startedAt).localeCompare(String(a.startedAt)));
  const byAdapter: Record<string, MigrationRunRow[]> = {};
  for (const row of rows) (byAdapter[row.datasetKey] ??= []).push(row);
  return byAdapter;
}

/**
 * @contract LegacyFileHealthCheckResult
 * Purpose: Browser-safe outcome of a Documents-grid source probe.
 * Fields:  ok, configured, errorType
 * Rules:   Never includes URL, SAS, or filename.
 * Sync:    Online exception — not persisted to Dexie.
 */
export interface LegacyFileHealthCheckResult {
  ok: boolean;
  configured: boolean;
  errorType?: string;
}

/**
 * @localFirst
 * Source of Truth: system-api (LEGACY_STORAGE_SAS_URL presence). Online exception.
 * Write Flow: none.
 * Sync Strategy: not cached in Dexie (config can change without a sync cycle).
 * Conflict Resolution: N/A
 */
/**
 * @contract DepartmentWideEstimateRow
 * Purpose: One occupancy adapter line on the department-wide confirm modal.
 * Fields:  key, displayName, count
 * Rules:   count is 0 when the table is missing; never fabricated.
 * Sync:    Online exception — not persisted to Dexie.
 */
export interface DepartmentWideEstimateRow {
  key: string;
  displayName: string;
  count: number;
}

export interface DepartmentWideEstimateResult {
  available: boolean;
  error?: string;
  adapters: DepartmentWideEstimateRow[];
}

/**
 * @localFirst
 * Source of Truth: system-api live SQL counts (online exception).
 * Write Flow: none.
 * Sync Strategy: confirm-modal session state only.
 * Conflict Resolution: N/A
 */
export async function fetchDepartmentWideEstimate(tenantId: string): Promise<DepartmentWideEstimateResult> {
  const result = await apiFetch(
    `/api/system/legacy-migration/department-wide/estimate?tenantId=${encodeURIComponent(tenantId)}`,
  );
  return {
    available: Boolean(result.available),
    error: typeof result.error === 'string' ? result.error : undefined,
    adapters: Array.isArray(result.adapters) ? result.adapters : [],
  };
}

export async function fetchLegacyStorageStatus(): Promise<{ configured: boolean }> {
  const result = await apiFetch('/api/system/legacy-migration/fileMetadata/storage-status');
  return { configured: Boolean(result.configured) };
}

/**
 * @localFirst
 * Source of Truth: system-api HEAD/probe of the composed source URL.
 * Write Flow: none. SAS stays server-side.
 * Sync Strategy: online exception; result held in component state.
 * Conflict Resolution: N/A
 *
 * @errorHandling
 * Strategy: Throws on non-OK HTTP; caller shows Unreachable / not configured.
 * User Impact: Health badge stays unknown on failure.
 * Logging: REQUIRED — caller logs; never log sourceUrl.
 */
export async function probeLegacyFileHealth(
  sourceUrl: string | null | undefined,
): Promise<LegacyFileHealthCheckResult> {
  const result = await apiFetch('/api/system/legacy-migration/fileMetadata/health-check', {
    method: 'POST',
    body: JSON.stringify({ sourceUrl: sourceUrl ?? null }),
  });
  return {
    ok: Boolean(result.ok),
    configured: Boolean(result.configured),
    errorType: typeof result.errorType === 'string' ? result.errorType : undefined,
  };
}

const DEPARTMENT_WIDE_KEY = 'department-wide';

const DEPARTMENT_WIDE_PROVISIONAL_STEPS: MigrationRunStepRow[] = [
  { adapterKey: 'referenceLookups', displayName: 'Reference Lookups', status: 'pending', percent: 0, counts: { selected: 0, created: 0, updated: 0, skipped: 0, failed: 0 } },
  { adapterKey: 'udfBlueprints', displayName: 'UDF Blueprints', status: 'pending', percent: 0, counts: { selected: 0, created: 0, updated: 0, skipped: 0, failed: 0 } },
  { adapterKey: 'inspectableEntities', displayName: 'Occupancies', status: 'pending', percent: 0, counts: { selected: 0, created: 0, updated: 0, skipped: 0, failed: 0 } },
  { adapterKey: 'occupancyContacts', displayName: 'Contacts', status: 'pending', percent: 0, counts: { selected: 0, created: 0, updated: 0, skipped: 0, failed: 0 } },
  { adapterKey: 'buildingInformationalItems', displayName: 'Building Intel', status: 'pending', percent: 0, counts: { selected: 0, created: 0, updated: 0, skipped: 0, failed: 0 } },
  { adapterKey: 'communicationLogs', displayName: 'Communication Logs', status: 'pending', percent: 0, counts: { selected: 0, created: 0, updated: 0, skipped: 0, failed: 0 } },
  { adapterKey: 'buildingSystems', displayName: 'Systems', status: 'pending', percent: 0, counts: { selected: 0, created: 0, updated: 0, skipped: 0, failed: 0 } },
  { adapterKey: 'masterChemicals', displayName: 'Master Chemicals', status: 'pending', percent: 0, counts: { selected: 0, created: 0, updated: 0, skipped: 0, failed: 0 } },
  { adapterKey: 'occupancyChemicals', displayName: 'Chemicals', status: 'pending', percent: 0, counts: { selected: 0, created: 0, updated: 0, skipped: 0, failed: 0 } },
  { adapterKey: 'occupancyTanks', displayName: 'Tanks', status: 'pending', percent: 0, counts: { selected: 0, created: 0, updated: 0, skipped: 0, failed: 0 } },
  { adapterKey: 'occupancyPermits', displayName: 'Permits', status: 'pending', percent: 0, counts: { selected: 0, created: 0, updated: 0, skipped: 0, failed: 0 } },
  { adapterKey: 'riskProfiles', displayName: 'Risk Assessment', status: 'pending', percent: 0, counts: { selected: 0, created: 0, updated: 0, skipped: 0, failed: 0 } },
  { adapterKey: 'tceCompliance', displayName: 'TCE', status: 'pending', percent: 0, counts: { selected: 0, created: 0, updated: 0, skipped: 0, failed: 0 } },
  { adapterKey: 'hydrantMain', displayName: 'Hydrants', status: 'pending', percent: 0, counts: { selected: 0, created: 0, updated: 0, skipped: 0, failed: 0 } },
  { adapterKey: 'crrProfile', displayName: 'CRR', status: 'pending', percent: 0, counts: { selected: 0, created: 0, updated: 0, skipped: 0, failed: 0 } },
  { adapterKey: 'crrDetector', displayName: 'CRR Detectors', status: 'pending', percent: 0, counts: { selected: 0, created: 0, updated: 0, skipped: 0, failed: 0 } },
  { adapterKey: 'crrDetectorEvent', displayName: 'CRR Detector Events', status: 'pending', percent: 0, counts: { selected: 0, created: 0, updated: 0, skipped: 0, failed: 0 } },
  { adapterKey: 'fileMetadata', displayName: 'Documents', status: 'pending', percent: 0, counts: { selected: 0, created: 0, updated: 0, skipped: 0, failed: 0 } },
];

/**
 * @localFirst
 * Source of Truth: Dexie legacy_migration_runs for this tenant where datasetKey is department-wide. Running/pending wins; otherwise the newest run including completed.
 * Write Flow: none (pure read for liveQuery).
 * Sync Strategy: fetchAndSyncRuns('department-wide') from the hook poller.
 * Conflict Resolution: server wins via bulkPut.
 */
export async function listActiveDepartmentWideRun(tenantId: string): Promise<MigrationRunRow | null> {
  const db = await getSystemDb();
  const all = await db.legacy_migration_runs.toArray();
  const rows = (all as MigrationRunRow[])
    .filter((r) => r.tenantId === tenantId && r.datasetKey === DEPARTMENT_WIDE_KEY && !r._deleted)
    .sort((a, b) => String(b.startedAt).localeCompare(String(a.startedAt)));
  const active = rows.find((r) => r.status === 'running' || r.status === 'pending');
  if (active) return active;
  return rows[0] ?? null;
}

/**
 * @contract DepartmentWideLiveJob
 * Purpose: Flyout-sourced live line for the department-wide header.
 * Fields:  message, percent, updatedAt, current, total
 * Rules:   Read-only Dexie projection of background_jobs; never written by the client.
 * Sync:    Server-authoritative backgroundJobs → Dexie liveQuery.
 */
export interface DepartmentWideLiveJob {
  message?: string;
  percent?: number;
  updatedAt?: string;
  current?: number;
  total?: number;
}

/**
 * @localFirst
 * Source of Truth: Dexie background_jobs for the active department-wide run.
 * Write Flow: none (pure read for liveQuery).
 * Sync Strategy: Mongo change-stream → Dexie; same feed as the flyout.
 * Conflict Resolution: server wins.
 */
export async function listActiveDepartmentWideLiveJob(runId: string | null | undefined): Promise<DepartmentWideLiveJob | null> {
  if (!runId) return null;
  const db = await getSystemDb();
  const jobs = await db.background_jobs.toArray();
  const job = jobs.find((row) => {
    const payload = (row as { payload?: { runId?: string } }).payload;
    return row.jobType === 'legacyMigration'
      && payload?.runId === runId
      && (row.status === 'running' || row.status === 'pending');
  });
  if (!job) return null;
  const progress = (job as { progress?: { percent?: number; current?: number; total?: number } }).progress;
  return {
    message: typeof job.message === 'string' ? job.message : undefined,
    percent: progress?.percent,
    updatedAt: typeof job.updatedAt === 'string' ? job.updatedAt : undefined,
    current: progress?.current,
    total: progress?.total,
  };
}

// ---------------------------------------------------------------------------
// Sync helpers — pull authoritative server state into Dexie for instant UI updates
// ---------------------------------------------------------------------------

/**
 * @localFirst
 * Source of Truth: system Mongo `legacyMigrationRuns` (server-authoritative).
 * Write Flow:      Fetches runs from GET /api/system/legacy-migration/runs and writes them
 *                  into system Dexie, replacing any provisional rows with authoritative data.
 * Sync Strategy:   Direct API fetch → Dexie write; complements background sync for instant feedback.
 * Conflict Resolution: Server wins — fetched rows overwrite local Dexie entries.
 *
 * Called from useLegacyRunHistory polling when any run is pending/running, and from
 * LegacyMigrationPage after a run is started.
 *
 * @errorHandling
 * Strategy:   Logs and swallows errors — polling is best-effort; background sync is the fallback.
 * User Impact: Transient — the next poll or background sync cycle will catch up.
 * Logging:    REQUIRED — log fetch/write failures.
 */
/**
 * @localFirst
 * Source of Truth: GET /runs/:runId then Dexie upsert.
 * Write Flow: authoritative run overwrites the local row (fills legacyDeptId after enqueue).
 * Sync Strategy: report-page no-store poller; liveQuery then re-emits.
 * Conflict Resolution: server wins; conditional browser-cache responses are bypassed for live progress.
 */
export async function fetchAndSyncRunById(runId: string): Promise<MigrationRunRow | null> {
  try {
    const result = await apiFetch(
      `/api/system/legacy-migration/runs/${encodeURIComponent(runId)}`,
      { cache: 'no-store' },
    );
    const run = result.run as MigrationRunRow | undefined;
    if (!run?._id) return null;
    const db = await getSystemDb();
    await db.legacy_migration_runs.put(run as any);
    return run;
  } catch (err) {
    console.warn('[legacyMigrationService] fetchAndSyncRunById failed:', err);
    return null;
  }
}

export async function fetchAndSyncRuns(
  legacyDeptId: number,
  datasetKey: string,
): Promise<void> {
  try {
    const result = await apiFetch(
      `/api/system/legacy-migration/runs?deptId=${legacyDeptId}&datasetKey=${encodeURIComponent(datasetKey)}`,
    );
    const runs = (result.runs ?? []) as MigrationRunRow[];
    if (runs.length === 0) return;

    const db = await getSystemDb();
    await db.legacy_migration_runs.putMany(runs as any);
  } catch (err) {
    console.warn('[legacyMigrationService] fetchAndSyncRuns failed:', err);
  }
}

/**
 * @localFirst
 * Source of Truth: system Mongo `legacyMigrationReceipts` (server-authoritative).
 * Write Flow:      Fetches receipts from GET /api/system/legacy-migration/receipts and writes
 *                  them into system Dexie for instant grid overlay updates.
 * Sync Strategy:   Direct API fetch → Dexie write; complements background sync for instant feedback.
 * Conflict Resolution: Server wins — fetched receipts overwrite local Dexie entries.
 *
 * Called from LegacyMigrationPage after a run completes so the grid immediately shows
 * "Migrated" badges without waiting for the next background sync cycle.
 *
 * @errorHandling
 * Strategy:   Logs and swallows errors — best-effort; background sync is the fallback.
 * User Impact: Transient — the next background sync cycle will catch up.
 * Logging:    REQUIRED — log fetch/write failures.
 */
export async function fetchAndSyncReceipts(
  legacyDeptId: number,
  datasetKey: string,
): Promise<void> {
  try {
    const result = await apiFetch(
      `/api/system/legacy-migration/receipts?deptId=${legacyDeptId}&datasetKey=${encodeURIComponent(datasetKey)}`,
    );
    const receipts = (result.receipts ?? []) as MigrationReceiptRow[];
    if (receipts.length === 0) return;

    const db = await getSystemDb();
    await db.legacy_migration_receipts.putMany(receipts as any);
  } catch (err) {
    console.warn('[legacyMigrationService] fetchAndSyncReceipts failed:', err);
  }
}
